/**
 * BOQ Changes Review Dialog
 * Shows pending changes from the other party with diff view.
 * Allows batch acknowledge or individual reject.
 */

import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Typography, Chip, Checkbox, TextField, Divider, Alert
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { brand, status as statusColors } from '../../theme/colors';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const changeTypeLabels = {
  item_edit: 'Item Edited',
  item_add: 'Item Added',
  item_remove: 'Item Removed',
  category_edit: 'Category Edited',
  category_add: 'Category Added',
  category_remove: 'Category Removed',
  labor_edit: 'Labor Cost Changed'
};

const changeTypeColors = {
  item_edit: statusColors.info,
  item_add: statusColors.success,
  item_remove: statusColors.error,
  category_edit: statusColors.info,
  category_add: statusColors.success,
  category_remove: statusColors.error,
  labor_edit: statusColors.warning
};

export default function BOQChangesReviewDialog({ open, onClose, changes = [], onAcknowledge, onReject, loading }) {
  const [selected, setSelected] = useState(new Set(changes.map(c => c.id)));
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === changes.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(changes.map(c => c.id)));
    }
  };

  const handleAcknowledge = () => {
    onAcknowledge(Array.from(selected));
  };

  const handleReject = () => {
    onReject(rejectingId, rejectReason);
    setRejectingId(null);
    setRejectReason('');
  };

  const renderDiff = (oldVals, newVals) => {
    const old = typeof oldVals === 'string' ? JSON.parse(oldVals) : (oldVals || {});
    const nw = typeof newVals === 'string' ? JSON.parse(newVals) : (newVals || {});
    const allKeys = [...new Set([...Object.keys(old), ...Object.keys(nw)])];

    return (
      <Box sx={{ mt: 1, pl: 1, borderLeft: `2px solid ${brand.primaryLight}40` }}>
        {allKeys.map(key => (
          <Box key={key} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 80, textTransform: 'capitalize' }}>
              {key.replace(/_/g, ' ')}:
            </Typography>
            {old[key] !== undefined && (
              <Typography variant="caption" sx={{
                textDecoration: nw[key] !== undefined ? 'line-through' : 'none',
                color: statusColors.error,
                fontFamily: 'monospace'
              }}>
                {typeof old[key] === 'number' ? fmt(old[key]) : String(old[key])}
              </Typography>
            )}
            {nw[key] !== undefined && old[key] !== undefined && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>→</Typography>
            )}
            {nw[key] !== undefined && (
              <Typography variant="caption" sx={{ color: statusColors.success, fontWeight: 600, fontFamily: 'monospace' }}>
                {typeof nw[key] === 'number' ? fmt(nw[key]) : String(nw[key])}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CompareArrowsIcon sx={{ color: brand.primary }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Review Changes</Typography>
        <Chip label={`${changes.length} pending`} size="small" sx={{ ml: 'auto', bgcolor: statusColors.warningBg, color: statusColors.warningDark }} />
      </DialogTitle>

      <DialogContent dividers>
        {changes.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No pending changes to review.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Checkbox
                checked={selected.size === changes.length}
                indeterminate={selected.size > 0 && selected.size < changes.length}
                onChange={toggleAll}
                size="small"
              />
              <Typography variant="caption" color="text.secondary">Select all</Typography>
            </Box>

            {changes.map((change, idx) => {
              const profile = change.changer_profile || {};
              return (
                <Box key={change.id}>
                  {idx > 0 && <Divider sx={{ my: 1.5 }} />}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Checkbox
                      checked={selected.has(change.id)}
                      onChange={() => toggleSelect(change.id)}
                      size="small"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={changeTypeLabels[change.change_type] || change.change_type}
                          size="small"
                          sx={{
                            bgcolor: (changeTypeColors[change.change_type] || statusColors.info) + '15',
                            color: changeTypeColors[change.change_type] || statusColors.info,
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          by {profile.first_name} {profile.last_name} · {new Date(change.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </Typography>
                      </Box>
                      {renderDiff(change.old_values, change.new_values)}

                      {rejectingId === change.id ? (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                          <TextField
                            size="small"
                            placeholder="Reason for rejection (optional)"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            sx={{ flex: 1 }}
                          />
                          <Button size="small" color="error" variant="contained" onClick={handleReject} sx={{ borderRadius: 1.5 }}>
                            Reject
                          </Button>
                          <Button size="small" onClick={() => setRejectingId(null)}>Cancel</Button>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setRejectingId(change.id)}
                          sx={{ mt: 0.5, fontSize: '0.7rem' }}
                        >
                          Reject this change
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Close</Button>
        <Button
          variant="contained"
          onClick={handleAcknowledge}
          disabled={selected.size === 0 || loading}
          sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark }, borderRadius: 2, px: 3 }}
        >
          Acknowledge {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
